// Hooks behind the shell's fit-the-window menu (lib/shellLayout.js has the
// rules; components/Layout.js uses both hooks).

import { useEffect, useRef, useState } from 'react'
import { FULL_QUERY, RAIL_QUERY, readViewportTier, focusableWithin, trapTabTarget } from '../lib/shellLayout'

// 'wide' | 'medium' | 'narrow', following the window live.  It re-renders the
// caller only when the window crosses a threshold — not on every resize event
// while a window edge is dragged (Layout is large).
export function useViewportTier () {
  const [tier, setTier] = useState(readViewportTier)
  const tierRef = useRef(tier)
  useEffect(() => {
    const update = () => {
      const next = readViewportTier()
      if (next === tierRef.current) return
      tierRef.current = next
      setTier(next)
    }
    const watched = []
    if (typeof window.matchMedia === 'function') {
      for (const q of [RAIL_QUERY, FULL_QUERY]) {
        try {
          const mql = window.matchMedia(q)
          if (typeof mql.addEventListener === 'function') mql.addEventListener('change', update)
          else if (typeof mql.addListener === 'function') mql.addListener(update)
          watched.push(mql)
        } catch { /* resize below still covers it */ }
      }
    }
    // Also on resize: an old engine without media-query change events, and a
    // cheap no-op otherwise (update() bails out unless the tier changed).
    window.addEventListener('resize', update)
    update()
    return () => {
      window.removeEventListener('resize', update)
      for (const mql of watched) {
        try {
          if (typeof mql.removeEventListener === 'function') mql.removeEventListener('change', update)
          else if (typeof mql.removeListener === 'function') mql.removeListener(update)
        } catch { /* ignore */ }
      }
    }
  }, [])
  return tier
}

// Focus handling for a menu drawn over the page (the ☰ drawer, or the icon
// rail opened for search), while `open`:
//   - focus moves into `containerRef` — focusTarget(root) when it returns an
//     element, else its [data-autofocus] element, else its first focusable;
//   - Tab / Shift+Tab stay inside it (focus that left it is pulled back);
//   - Escape calls onClose — unless focus is in another dialog on top (a
//     confirm opened from the menu answers its own Escape and Tab);
//   - the page underneath does not scroll (`lockScroll`, default true; the
//     rail's ⭐ popover passes false — it covers no page, and hiding the
//     page's scrollbar would shift the page sideways under it).
// When it closes, focus goes back where it was before it opened (else
// returnFocusRef) — only if focus is now lost (on <body>) or still inside a
// container that is no longer shown (`keepFocusInside` false: the drawer).
export function useOverlayMenu ({ open, containerRef, onClose, focusTarget, returnFocusRef, keepFocusInside = false, lockScroll = true }) {
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose
  const focusTargetRef = useRef(focusTarget)
  focusTargetRef.current = focusTarget
  const keepInsideRef = useRef(keepFocusInside)
  keepInsideRef.current = keepFocusInside
  const returnToRef = useRef(null)
  const wasOpenRef = useRef(false)

  // Opening: remember focus, move it in, stop the page scrolling.
  useEffect(() => {
    if (!open) return undefined
    const active = document.activeElement
    returnToRef.current = active && active !== document.body ? active : null
    const root = containerRef.current
    const pick = () => {
      let target = null
      try { target = typeof focusTargetRef.current === 'function' ? focusTargetRef.current(root) : null } catch { target = null }
      return target || (root && root.querySelector('[data-autofocus]')) || focusableWithin(root)[0] || root
    }
    const focusIn = () => {
      const target = pick()
      if (target && typeof target.focus === 'function') {
        try { target.focus() } catch { /* ignore */ }
      }
    }
    // Now (the menu became visible in this commit), then again on each of the
    // next few frames while focus has not arrived — a browser can refuse
    // focus() on an element that is still finishing a style change (a
    // visibility or transform transition) — but never once the person has
    // put focus somewhere else themselves.
    const opener = returnToRef.current
    const stillWaiting = () => {
      const a = document.activeElement
      if (root && root.contains(a)) return false
      return !a || a === document.body || a === opener
    }
    focusIn()
    let raf = null
    let tries = 0
    const retry = () => {
      raf = null
      if (!stillWaiting() || ++tries > 10) return
      focusIn()
      raf = window.requestAnimationFrame(retry)
    }
    if (stillWaiting() && typeof window.requestAnimationFrame === 'function') raf = window.requestAnimationFrame(retry)
    const lock = lockScroll !== false
    const prevOverflow = document.body.style.overflow
    if (lock) document.body.style.overflow = 'hidden'
    return () => {
      if (raf != null && typeof window.cancelAnimationFrame === 'function') window.cancelAnimationFrame(raf)
      if (lock) document.body.style.overflow = prevOverflow
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // Escape + the Tab trap.
  useEffect(() => {
    if (!open) return undefined
    const inOtherDialog = (el, root) => {
      if (!el || !el.closest || (root && root.contains(el))) return false
      return !!el.closest('[role="dialog"], [role="alertdialog"], [aria-modal="true"]')
    }
    // A modal opened on top (useAsk's confirm, Quick Intake…) owns the keys
    // even before it has taken focus (useAsk focuses after 30 ms).
    const otherModalOpen = (root) => Array.from(document.querySelectorAll('[aria-modal="true"]'))
      .some((el) => el !== root && !root.contains(el))
    const onKey = (e) => {
      const root = containerRef.current
      if (!root) return
      const active = document.activeElement
      if (inOtherDialog(active, root) || otherModalOpen(root)) return
      if (e.key === 'Escape' && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault()
        // Calendar's "Esc leaves full screen" listens on window: this Esc
        // only closes the menu.
        e.stopPropagation()
        if (onCloseRef.current) onCloseRef.current('escape')
        return
      }
      if (e.key === 'Tab' && !e.altKey && !e.ctrlKey && !e.metaKey) {
        const next = trapTabTarget(root, active, e.shiftKey)
        if (next) {
          e.preventDefault()
          try { next.focus() } catch { /* ignore */ }
        }
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // Closing: give focus back.
  useEffect(() => {
    if (open) { wasOpenRef.current = true; return undefined }
    if (!wasOpenRef.current) return undefined
    wasOpenRef.current = false
    const root = containerRef.current
    const active = document.activeElement
    const keepInside = keepInsideRef.current
    const lost = !active || active === document.body
    const strandedInside = !keepInside && root && root.contains(active)
    if (!lost && !strandedInside) return undefined
    const candidates = [returnToRef.current, returnFocusRef && returnFocusRef.current]
    returnToRef.current = null
    const giveBack = () => {
      for (const el of candidates) {
        if (!el || !el.isConnected || typeof el.focus !== 'function') continue
        // Never back into a container that is no longer shown (the drawer).
        if (!keepInside && root && root.contains(el)) continue
        try { el.focus() } catch { continue }
        if (document.activeElement === el) return true
      }
      return false
    }
    if (giveBack() || typeof window.requestAnimationFrame !== 'function') return undefined
    // As on opening: a browser can refuse focus() for a frame while a style
    // change settles.  Retry while focus is still lost, a few frames at most.
    let tries = 0
    let raf = window.requestAnimationFrame(function retry () {
      raf = null
      const a = document.activeElement
      const stillLost = !a || a === document.body || (!keepInside && root && root.contains(a))
      if (!stillLost || ++tries > 10 || giveBack()) return
      raf = window.requestAnimationFrame(retry)
    })
    return () => { if (raf != null && typeof window.cancelAnimationFrame === 'function') window.cancelAnimationFrame(raf) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])
}
